import { debounce } from "@app/utils/debounce";

class Carousel extends HTMLElement {
  $carousel: Element;
  $prevButton: Element | null;
  $nextButton: Element | null;
  $scrollProgressWrapper: Element | null;
  $scrollProgress: HTMLDivElement | null;
  $thumbs: NodeListOf<HTMLDivElement> | null;

  hideButtonOnEdge: boolean;

  currentIndex: number = 0;

  constructor() {
    super();
    this.$carousel = this.querySelector(".carousel-main")!;
    this.$prevButton = this.querySelector(".carousel-button-left");
    this.$nextButton = this.querySelector(".carousel-button-right");
    this.$scrollProgressWrapper = this.querySelector(".scroll-progress-wrapper");
    this.$scrollProgress = this.querySelector(".scroll-progress");
    this.$thumbs = this.querySelectorAll(".carousel-thumb")!;

    // Hides the prev/next button on the first/last slide
    this.hideButtonOnEdge = this.hasAttribute("data-hide-button-on-edge");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Next/prev button visibility
    if (this.hideButtonOnEdge) {
      this.updateButtonVisibility();
      const debouncedUpdateButtonVisibility = debounce(this.updateButtonVisibility.bind(this), 100);
      this.$carousel.addEventListener(
        "scroll",
        () => {
          debouncedUpdateButtonVisibility();
        },
        { passive: true },
      );
    }

    // Scroll progress
    if (this.$scrollProgress) {
      this.updateScrollProgress();
      this.$carousel?.addEventListener(
        "scroll",
        () => {
          this.updateScrollProgress();
        },
        { passive: true },
      );
    }

    // Index change event
    this.updateThumbs();
    const debouncedUpdateIndex = debounce(this.updateIndex.bind(this), 100);
    this.$carousel?.addEventListener(
      "scroll",
      () => {
        debouncedUpdateIndex();
      },
      { passive: true },
    );

    // Next button click event
    this.$nextButton?.addEventListener("click", () => {
      this.$carousel.scrollBy({
        left: this.$carousel.clientWidth / 2,
        behavior: "smooth",
      });
      const { scrollLeft = 0, scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};
      if (scrollLeft + clientWidth - scrollWidth > -1) {
        this.$carousel.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    });

    // Prev button click event
    this.$prevButton?.addEventListener("click", () => {
      this.$carousel.scrollBy({
        left: -this.$carousel.clientWidth / 2,
        behavior: "smooth",
      });
      const { scrollLeft = 0, scrollWidth = 0 } = this.$carousel || {};
      if (scrollLeft < 1) {
        this.$carousel.scrollTo({
          left: scrollWidth,
          behavior: "smooth",
        });
      }
    });

    // Thumbs click event
    this.$thumbs?.forEach(($thumb, index) => {
      $thumb.addEventListener("click", () => {
        this.$carousel.scrollTo({
          left: this.$carousel.clientWidth * index,
          behavior: "smooth",
        });
      });
    });
  }

  updateIndex() {
    this.currentIndex = Math.floor(this.$carousel.scrollLeft / this.$carousel.clientWidth);
    this.updateThumbs();
  }

  updateButtonVisibility() {
    const { scrollLeft = 0, scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};

    // Update next button
    if (Math.abs(scrollLeft + clientWidth - scrollWidth) < 30) {
      this.$nextButton?.classList.add("!hidden");
    } else {
      this.$nextButton?.classList.remove("!hidden");
    }

    // Update prev button
    if (scrollLeft === 0) {
      this.$prevButton?.classList.add("!hidden");
    } else {
      this.$prevButton?.classList.remove("!hidden");
    }
  }

  updateScrollProgress() {
    if (!this.$scrollProgress) return;
    const { scrollLeft = 0, scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};
    this.$scrollProgress.style.width = `${(clientWidth / scrollWidth) * 100}%`;
    this.$scrollProgress.style.left = `${(scrollLeft / scrollWidth) * 100}%`;
  }

  updateThumbs() {
    this.$thumbs?.forEach(($thumb, index) => {
      if (this.currentIndex === index) {
        $thumb.classList.add("is-active");
      } else {
        $thumb.classList.remove("is-active");
      }
    })
  }
}

window.customElements.define("custom-carousel", Carousel);
